import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  TextFieldProps,
} from '@mui/material';

import {
  COURSE_STATUS_OPTIONS,
  getCoursesState,
  TCourseStatus,
  TUpdateCourseAsAdmin,
} from '../../../../../../store/V2/CoursesSlice';
import { useAppSelector } from '../../../../../../hooks';
import { updateCourseAsAdminSavingSlice } from '../../../../../../store/V2/SavingSlice';
import { ICoursePermissionTemplate } from '../../../../../../store/V2/CoursePermissionTemplatesSlice';
import CoursePermissionTemplateDropdown, {
  TCoursePermissionTemplateOption,
} from '../../../../../../components/Inputs/admin/CoursePermissionTemplateDropdown';

interface Step4Props {
  moveToPreviousStep: () => void;
  onFinish: () => void;
}

type TState = {
  visibility: boolean;
  selfEnrolement: boolean;
  publish: boolean;
  selectedPermissionTemplate: TCoursePermissionTemplateOption | null;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  visibility: false,
  selfEnrolement: false,
  publish: false,
  selectedPermissionTemplate: null,
};

const Step4: React.FC<Step4Props> = ({ moveToPreviousStep, onFinish }) => {
  const dispatch = useDispatch();

  // Store
  const coursesState = useAppSelector(getCoursesState);

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  const courseData = coursesState.adminCourse;

  useEffect(() => {
    if (courseData) {
      const coursePermissionTemplateData = courseData.permissionTemplate as
        | ICoursePermissionTemplate
        | undefined;

      setState((prevState) => {
        const newState = { ...prevState };

        if (coursePermissionTemplateData) {
          newState.selectedPermissionTemplate = {
            label: coursePermissionTemplateData.name,
            value: coursePermissionTemplateData._id,
          };
        }

        return newState;
      });
    }
  }, []);

  const toggleLoading = () => {
    setLoading((prevState) => !prevState);
  };

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleSubmit = async () => {
    if (courseData) {
      toggleLoading();

      const apiData: TUpdateCourseAsAdmin = {
        _id: courseData._id,
        visibility: state.visibility,
        selfEnrolement: state.selfEnrolement,
        status: COURSE_STATUS_OPTIONS.DRAFT as TCourseStatus,
        isStep4Completed: true,
      };

      if (state.selectedPermissionTemplate) {
        apiData.permissionTemplate = state.selectedPermissionTemplate.value;
      }

      await dispatch(updateCourseAsAdminSavingSlice(apiData));

      toggleLoading();

      onFinish();
    }
  };

  return (
    <div>
      <div style={{ minHeight: '350px' }}>
        <Card
          elevation={0}
          sx={{ backgroundColor: '#F8F9FD', borderRadius: '10px', mb: '20px' }}
        >
          <CardContent style={{ padding: '16px' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormGroup>
                  <FormControlLabel
                    disabled={isLoading || courseData?.isOneToOne}
                    label="Show in Landing Page"
                    control={
                      <Checkbox
                        checked={state.visibility}
                        onChange={(event, value) =>
                          updateStateAttributes({ visibility: value })
                        }
                      />
                    }
                  />
                  <FormControlLabel
                    disabled={isLoading || courseData?.isOneToOne}
                    label="Students can enroll themselves"
                    control={
                      <Checkbox
                        checked={state.selfEnrolement}
                        onChange={(event, value) =>
                          updateStateAttributes({ selfEnrolement: value })
                        }
                      />
                    }
                  />
                  <FormControlLabel
                    disabled={isLoading}
                    label="Publish"
                    control={
                      <Checkbox
                        checked={state.publish}
                        onChange={(event, value) =>
                          updateStateAttributes({ publish: value })
                        }
                      />
                    }
                  />
                </FormGroup>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <div>
          <Grid container>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <CoursePermissionTemplateDropdown
                  selectedItem={state.selectedPermissionTemplate}
                  handleChange={(e, value) =>
                    updateStateAttributes({
                      selectedPermissionTemplate: value,
                    })
                  }
                  componentProps={{
                    fullWidth: true,
                  }}
                  textFieldProps={{
                    label: 'Permission Template',
                    placeholder: 'Select',
                    ...textFieldProps,
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </div>
      </div>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt="15px"
      >
        <Button variant="text" color="primary" onClick={moveToPreviousStep}>
          Back
        </Button>
        <LoadingButton
          variant="text"
          color="primary"
          onClick={handleSubmit}
          loading={isLoading}
        >
          Complete
        </LoadingButton>
      </Stack>
    </div>
  );
};

export default Step4;
